import React, { useRef, useState, useEffect, useMemo } from "react";
import { Play, Pause } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

// Only one audio plays at a time across the whole app.
const PLAY_EVENT = "auramax-audio-play";

function bars(seed, n = 36) {
  const s = String(seed || "a");
  let h = 0;
  const out = [];
  for (let i = 0; i < n; i++) {
    h = (Math.sin((s.charCodeAt(i % s.length) || 65) * (i + 7) * 12.9898) * 43758.5453) % 1;
    out.push(0.25 + Math.abs(h) * 0.75);
  }
  return out;
}

function fmtTime(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function AudioPlayer({ src, duration, compact = false, testid }) {
  const audioRef = useRef(null);
  const id = useRef(Math.random().toString(36).slice(2));
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(duration || 0);
  const peaks = useMemo(() => bars(src, compact ? 28 : 40), [src, compact]);

  useEffect(() => {
    const onOther = (e) => { if (e.detail !== id.current && audioRef.current && !audioRef.current.paused) audioRef.current.pause(); };
    window.addEventListener(PLAY_EVENT, onOther);
    return () => window.removeEventListener(PLAY_EVENT, onOther);
  }, []);

  const toggle = (e) => {
    e?.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: id.current })); a.play().catch(() => {}); }
    else a.pause();
  };

  const seek = (e) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * dur;
  };

  const progress = dur ? cur / dur : 0;

  return (
    <div data-testid={testid || "audio-player"} className={cn("flex items-center gap-3 rounded-2xl border border-am-border bg-am-surface2", compact ? "p-2" : "p-3")}>
      <audio
        ref={audioRef}
        src={mediaUrl(src)}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCur(0); }}
        onTimeUpdate={(e) => setCur(e.target.currentTime)}
        onLoadedMetadata={(e) => { if (isFinite(e.target.duration)) setDur(e.target.duration); }}
      />
      <button onClick={toggle} data-testid="audio-toggle" className={cn("grid shrink-0 place-items-center rounded-full aura-gradient text-black active:scale-90", compact ? "h-9 w-9" : "h-11 w-11")}>
        {playing ? <Pause className={compact ? "h-4 w-4" : "h-5 w-5"} fill="currentColor" /> : <Play className={cn(compact ? "h-4 w-4" : "h-5 w-5", "translate-x-[1px]")} fill="currentColor" />}
      </button>
      <div className="min-w-0 flex-1">
        <div onClick={seek} className="flex h-9 cursor-pointer items-center gap-[2px]">
          {peaks.map((p, i) => {
            const on = i / peaks.length <= progress;
            return <span key={i} className={cn("flex-1 rounded-full transition-colors", on ? "" : "bg-am-surface3")} style={{ height: `${p * 100}%`, background: on ? "linear-gradient(180deg,#ff2e93,#ffd23c,#38e1ff)" : undefined }} />;
          })}
        </div>
      </div>
      <span className="shrink-0 font-mono text-xs tabular-nums text-am-text2">{fmtTime(playing || cur ? cur : dur)}</span>
    </div>
  );
}
